/**
 * Tax "R" Us Clara Intelligence — App Registry
 * Complete definition of all apps, pages, and navigation structure
 * Ported from shell.js appRegistry object
 */

import type { AppRegistry } from '../types';

export const appRegistry: AppRegistry = {
  'tru-clara-app': {
    label: 'KPMG Communications',
    short: 'Communications',
    headerName: 'communications',
    kcBranded: true,
    homePage: 'action-tray',
    hasSubNav: true,
    pages: {
      'action-tray': {
        title: 'Current Page with Action Tray',
        desc: 'Track and manage open action items across your engagements.',
        icon: 'task_alt',
        color: 'blue',
        subPages: [
          { id: 'at-overview', title: 'Action Overview', desc: 'See all actions at a glance with status and priority.', icon: 'dashboard', color: 'blue' },
          { id: 'at-pending', title: 'Pending Actions', desc: 'Review items awaiting completion or approval.', icon: 'pending_actions', color: 'blue' },
          { id: 'at-completed', title: 'Completed Actions', desc: 'View resolved actions and completion history.', icon: 'task_alt', color: 'blue' },
        ],
      },
      'no-action-tray': {
        title: 'Current Page',
        desc: 'General workspace for the current engagement context.',
        icon: 'description',
        color: 'blue',
      },
      'engagements': {
        title: 'Engagements Management Tool',
        desc: 'Create, assign, and monitor client engagements end to end.',
        icon: 'manage_accounts',
        color: 'blue',
        subPages: [
          { id: 'eng-active', title: 'Active Engagements', desc: 'View engagements currently in progress.', icon: 'work', color: 'blue' },
          { id: 'eng-archive', title: 'Archived Engagements', desc: 'Access completed or closed engagements.', icon: 'inventory', color: 'blue' },
          { id: 'eng-settings', title: 'Engagement Settings', desc: 'Configure roles, deadlines, and notifications.', icon: 'settings', color: 'blue' },
        ],
      },
      'risk-assessment': {
        title: 'Risk Assessment Tool',
        desc: 'Identify, score, and mitigate audit risks.',
        icon: 'shield',
        color: 'blue',
      },
      'data-analytics': {
        title: 'Data Analytics Dashboard',
        desc: 'Visualize trends and run queries on engagement data.',
        icon: 'analytics',
        color: 'blue',
        subPages: [
          { id: 'da-reports', title: 'Saved Reports', desc: 'Access previously generated analytics reports.', icon: 'assessment', color: 'blue' },
          { id: 'da-queries', title: 'Custom Queries', desc: 'Build and run ad-hoc data queries.', icon: 'query_stats', color: 'blue' },
        ],
      },
      'rebate-analyzer': {
        title: 'Rebate Analyzer Activity',
        desc: 'Track rebate analyzer tasks, statuses, and recent activity across projects.',
        icon: 'monitoring',
        color: 'blue',
      },
    },
  },

  'tru-clara-ai': {
    label: 'KPMG Workspace',
    short: 'Workspace',
    headerName: 'workspace',
    kcBranded: true,
    homePage: 'eng-pink-garments',
    hasSubNav: true,
    removableTabs: true,
    hasChatBar: true,
    fixedTabs: [
      { id: '__today__', label: 'New Chat', icon: 'ads_click' },
      { id: '__home__', label: null, icon: 'domain' },
    ],
    pages: {
      'eng-alphago': {
        title: 'AlphaGo FY28',
        desc: 'AlphaGo engagement for fiscal year 2028.',
        icon: 'work',
        color: 'blue',
      },
      'eng-pink-garments': {
        title: 'Pink Garments FY2029',
        desc: 'Pink Garments engagement for fiscal year 2029.',
        icon: 'work',
        color: 'blue',
      },
      'eng-zephyra': {
        title: 'Zephyra Mobility US 2028',
        desc: 'Zephyra Mobility US engagement for 2028.',
        icon: 'work',
        color: 'blue',
      },
    },
  },

  'kc-workflow': {
    label: 'KPMG Workflow',
    short: 'Workflow',
    headerName: 'workflow',
    homePage: 'engagements-dashboard',
    pages: {
      'engagements-dashboard': {
        title: 'Engagement Dashboard',
        desc: 'Real-time overview of all active audit engagements and milestones.',
        icon: 'dashboard',
        color: 'blue',
        subPages: [
          { id: 'ed-overview', title: 'Overview', desc: 'Summary of all active engagements and key metrics.', icon: 'monitoring', color: 'blue' },
          { id: 'ed-milestones', title: 'Milestones', desc: 'Track engagement milestones and deadlines.', icon: 'flag', color: 'blue' },
          { id: 'ed-team', title: 'Team Activity', desc: 'View team workload and recent activity.', icon: 'groups', color: 'blue' },
        ],
      },
      'report-retain': {
        title: 'KPMG Report & Retain',
        desc: 'Manage document retention and archival for completed engagements.',
        icon: 'folder_managed',
        color: 'blue',
      },
      'engagements-mgmt': {
        title: 'Engagements Management Tool',
        desc: 'Create, assign, and track audit engagement workflows.',
        icon: 'manage_accounts',
        color: 'blue',
        subPages: [
          { id: 'em-active', title: 'Active Engagements', desc: 'View and manage engagements currently in progress.', icon: 'work', color: 'blue' },
          { id: 'em-create', title: 'New Engagement', desc: 'Set up a new audit engagement with guided workflow.', icon: 'note_add', color: 'blue' },
          { id: 'em-settings', title: 'Engagement Settings', desc: 'Configure roles, deadlines, and notifications.', icon: 'settings', color: 'blue' },
        ],
      },
      'clara-compass': {
        title: 'KPMG Clara Compass',
        desc: 'AI-powered guidance for audit planning and risk assessment.',
        icon: 'assistant_navigation',
        color: 'blue',
        subPages: [
          { id: 'cc-risk-map', title: 'Risk Map', desc: 'Visual risk assessment across engagement areas.', icon: 'map', color: 'blue' },
          { id: 'cc-planning', title: 'Planning Guide', desc: 'Step-by-step audit planning recommendations.', icon: 'route', color: 'blue' },
        ],
      },
      'kpmg-extend': {
        title: 'KPMG Extend',
        desc: 'Extend audit capabilities with advanced analytics and integrations.',
        icon: 'extension',
        color: 'blue',
      },
      'data-workbench': {
        title: 'Data Workbench',
        desc: 'Transform and analyze engagement data for audit procedures.',
        icon: 'workspaces',
        color: 'blue',
        subPages: [
          { id: 'dw-sources', title: 'Data Sources', desc: 'Connect and manage engagement data sources.', icon: 'database', color: 'blue' },
          { id: 'dw-transforms', title: 'Transformations', desc: 'Build data pipelines and transformations.', icon: 'transform', color: 'blue' },
          { id: 'dw-analysis', title: 'Analysis', desc: 'Run queries and analyze processed data.', icon: 'query_stats', color: 'blue' },
        ],
      },
      'feedback': {
        title: 'Feedback',
        desc: 'Submit and track feedback on audit processes and tools.',
        icon: 'feedback',
        color: 'blue',
      },
      'create-engagement': {
        title: 'Create Engagement',
        desc: 'Initiate new audit engagements with guided setup.',
        icon: 'note_add',
        color: 'blue',
      },
      'center-audit-solutions': {
        title: 'Center for Audit Solutions',
        desc: 'Centralized hub for audit methodologies, tools, and best practices.',
        icon: 'hub',
        color: 'green',
        launch: true,
      },
      'audit-tech-navigator': {
        title: 'Audit Tech Navigator',
        desc: 'Explore and adopt technology solutions for audit engagements.',
        icon: 'explore',
        color: 'green',
        launch: true,
      },
      'audit-reports-tool': {
        title: 'Audit Reports Tool (ART)',
        desc: 'Draft, review, and finalize audit reports with standardized templates.',
        icon: 'assessment',
        color: 'green',
        launch: true,
      },
      'alex': {
        title: 'Alex',
        desc: 'Intelligent assistant for audit research and knowledge management.',
        icon: 'smart_toy',
        color: 'green',
        launch: true,
      },
    },
  },

  'tru-workpaper': {
    label: 'KPMG Clara Digital Gateway',
    short: 'Digital Gateway',
    headerName: 'digital gateway',
    homePage: 'wp-templates',
    hasSubNav: true,
    pages: {
      'wp-templates': {
        title: 'Workpaper Templates',
        desc: 'Start from standardized or custom workpaper formats.',
        icon: 'content_copy',
        color: 'blue',
        subPages: [
          { id: 'wpt-standard', title: 'Standard Templates', desc: 'Firm-approved templates for common procedures.', icon: 'description', color: 'blue' },
          { id: 'wpt-custom', title: 'Custom Templates', desc: 'Templates created by your team for specific needs.', icon: 'edit_document', color: 'blue' },
        ],
      },
      'wp-archive': {
        title: 'Archive & Retention',
        desc: 'Manage retention policies and archived workpapers.',
        icon: 'archive',
        color: 'blue',
      },
      'wp-review': {
        title: 'Review Queue',
        desc: 'Workpapers pending reviewer sign-off.',
        icon: 'checklist',
        color: 'blue',
      },
    },
  },

  'tru-inventory': {
    label: 'KPMG Clara Project Monitoring',
    short: 'Project Monitoring',
    headerName: 'project monitoring',
    homePage: 'inv-dashboard',
    pages: {
      'inv-audit-log': {
        title: 'Audit Log',
        desc: 'Full history of inventory changes and approvals.',
        icon: 'history',
        color: 'blue',
      },
      'inv-dashboard': {
        title: 'Inventory Dashboard',
        desc: 'Real-time overview of inventory levels and status.',
        icon: 'inventory_2',
        color: 'blue',
      },
      'inv-adjustments': {
        title: 'Stock Adjustments',
        desc: 'Record and approve inventory corrections.',
        icon: 'swap_vert',
        color: 'blue',
      },
    },
  },

  'tru-tasks': {
    label: 'KPMG Clara Task Manager',
    short: 'Task Manager',
    headerName: 'task manager',
    homePage: 'task-board',
    pages: {
      'task-board': {
        title: 'Task Board',
        desc: 'Organize and prioritize tasks across your team.',
        icon: 'assignment',
        color: 'blue',
      },
      'task-reports': {
        title: 'Progress Reports',
        desc: 'Track team velocity and completion rates.',
        icon: 'bar_chart',
        color: 'blue',
      },
      'task-calendar': {
        title: 'Deadline Calendar',
        desc: 'View upcoming deadlines on a calendar timeline.',
        icon: 'calendar_month',
        color: 'blue',
      },
    },
  },

  'tru-quality': {
    label: 'KPMG Clara Quality Control',
    short: 'Quality Control',
    headerName: 'quality control',
    homePage: 'qc-checklists',
    pages: {
      'qc-checklists': {
        title: 'QC Checklists',
        desc: 'Run through standardized quality review steps.',
        icon: 'fact_check',
        color: 'blue',
      },
      'qc-standards': {
        title: 'Standards Library',
        desc: 'Reference firm quality standards and guidelines.',
        icon: 'verified',
        color: 'blue',
      },
      'qc-findings': {
        title: 'Findings Tracker',
        desc: 'Log and resolve quality control findings.',
        icon: 'bug_report',
        color: 'blue',
      },
    },
  },

  'tru-vault': {
    label: 'KPMG Clara Document Vault',
    short: 'Document Vault',
    headerName: 'document vault',
    homePage: 'vault-browse',
    pages: {
      'vault-browse': {
        title: 'Browse Documents',
        desc: 'Search and navigate your document repository.',
        icon: 'folder_open',
        color: 'blue',
      },
      'vault-retention': {
        title: 'Retention Manager',
        desc: 'Set and enforce document retention schedules.',
        icon: 'lock_clock',
        color: 'blue',
      },
      'vault-upload': {
        title: 'Upload Center',
        desc: 'Upload and classify new documents.',
        icon: 'cloud_upload',
        color: 'blue',
      },
    },
  },

  'tru-close': {
    label: 'KPMG Clara Financial Close',
    short: 'Financial Close',
    headerName: 'financial close',
    homePage: 'close-checklist',
    pages: {
      'close-checklist': {
        title: 'Close Checklist',
        desc: 'Step through the month-end close process.',
        icon: 'account_balance',
        color: 'blue',
      },
      'close-reconciliations': {
        title: 'Reconciliations',
        desc: 'Match and verify account balances.',
        icon: 'compare_arrows',
        color: 'blue',
      },
      'close-journal': {
        title: 'Journal Entries',
        desc: 'Prepare and post adjusting entries.',
        icon: 'edit_note',
        color: 'blue',
      },
    },
  },

  'tru-analytics': {
    label: 'KPMG Clara Performance Analytics',
    short: 'Performance Analytics',
    headerName: 'performance analytics',
    homePage: 'perf-dashboards',
    pages: {
      'perf-dashboards': {
        title: 'KPI Dashboards',
        desc: 'Monitor key performance indicators at a glance.',
        icon: 'monitoring',
        color: 'blue',
      },
      'perf-benchmarks': {
        title: 'Benchmarking',
        desc: 'Compare performance against industry benchmarks.',
        icon: 'leaderboard',
        color: 'blue',
      },
      'perf-custom': {
        title: 'Custom Reports',
        desc: 'Build tailored reports for specific analysis needs.',
        icon: 'tune',
        color: 'blue',
      },
    },
  },
};

export function getAppConfig(appId: string) {
  return appRegistry[appId] || null;
}

export function getPageConfig(appId: string, pageId: string) {
  const app = appRegistry[appId];
  return app?.pages[pageId] || null;
}

export function getAllApps() {
  return Object.entries(appRegistry).map(([id, config]) => ({
    id,
    ...config,
  }));
}
